// downloadQuotationExcel.js
// Generates a .xlsx quotation that mirrors QuotationPDF_2_cubix layout
// Usage: import { downloadQuotationExcel } from "./downloadQuotationExcel";
//        downloadQuotationExcel({ selectedLead, formattedDate, productsInQuotationFromAPI, vatValue, discountValue, alreadySentQuotationNumber, salesOrderYesOrNo });
// Requires: npm install xlsx

import * as XLSX from "xlsx";

export const downloadQuotationExcel = ({
  selectedLead,
  formattedDate,
  productsInQuotationFromAPI,
  vatValue,
  discountValue,
  alreadySentQuotationNumber,
  salesOrderYesOrNo,
}) => {
  if (!productsInQuotationFromAPI?.length) {
    alert("No quotation data to export.");
    return;
  }

  const first = productsInQuotationFromAPI[0];
  const isCubix =
    (localStorage.getItem("companyCode") || "").trim().toUpperCase() === "CUBIX";

  // ── Helpers ──────────────────────────────────────────────────────────────
  const refNo = alreadySentQuotationNumber
    ? alreadySentQuotationNumber
    : salesOrderYesOrNo?.isthisSalesOrder
    ? selectedLead?.Salesorder_No
    : selectedLead?.Proposal_No || first?.quotation_number || "";

  const docTitle = salesOrderYesOrNo?.isthisSalesOrder
    ? "SALES ORDER"
    : "QUOTATION";

  // Filter out OPT/OPTION rows for totals
  const billableItems = productsInQuotationFromAPI.filter(
    (i) => i.itemCustRef !== "OPT" && i.itemCustRef !== "OPTION"
  );

  const totalValue = billableItems.reduce(
    (acc, i) => acc + (i.itemFoc === "FOC" ? 0 : i.itemQuantity * i.itemPrice),
    0
  );
  const afterDiscount = totalValue - (discountValue || 0);
  const vatAmount =
    vatValue > 0 ? (afterDiscount * first?.dynamicVatFromAPI) / 100 : 0;
  const netValue = afterDiscount + vatAmount;

  // ── Build rows ────────────────────────────────────────────────────────────
  const aoa = []; // array-of-arrays

  // Row 1 – document type title
  aoa.push([docTitle]);

  // Row 2 – blank
  aoa.push([]);

  // Row 3-6 – header info block
  aoa.push(["To:", first?.customer_name_from_details?.toUpperCase() || ""]);
  aoa.push(["Attn:", first?.head1 || ""]);
  aoa.push([
    "Email / Phone:",
    `${first?.Lead_email || ""} | ${first?.Lead_Phone || ""}`,
  ]);
  aoa.push([]);
  aoa.push(["Ref. No.:", refNo, "", "Date:", formattedDate]);
  if (!selectedLead?.Salesorder_No && first?.revision_number) {
    aoa.push(["Rev. No.:", first.revision_number]);
  }
  aoa.push(["Project:", first?.project_name_from_details || ""]);
  aoa.push([]);

  // ── Table header ──────────────────────────────────────────────────────────
  const tableHeaderRow = ["S.N"];
  if (!isCubix) tableHeaderRow.push("Ref/Type", "Article No");
  tableHeaderRow.push("Description", "Unit", "Qty", "Unit Price", "Value");
  aoa.push(tableHeaderRow);

  const tableHeaderRowIndex = aoa.length; // 1-based for XLSX merge later

  // ── Table body ────────────────────────────────────────────────────────────
  productsInQuotationFromAPI.forEach((item, idx) => {
    const isOpt =
      item.itemCustRef === "OPT" || item.itemCustRef === "OPTION";
    const isFoc = item.itemFoc === "FOC";

    const row = [idx + 1];
    if (!isCubix) row.push(item.itemCustRef || "", item.itemCode || "");
    row.push(
      item.itemDescription || "",
      item.itemUnit || "",
      item.itemQuantity ?? "",
      isFoc ? 0 : parseFloat(item.itemPrice || 0).toFixed(2),
      isFoc ? 0 : isOpt ? "" : parseFloat(item.itemAmount || 0).toFixed(2)
    );
    aoa.push(row);
  });

  aoa.push([]); // spacer

  // ── Summary block ─────────────────────────────────────────────────────────
  const valueCol = isCubix ? 6 : 8; // column index (0-based) for right-aligned values

  const pushSummaryRow = (label, value) => {
    const row = new Array(valueCol - 1).fill("");
    row.push(label, value);
    aoa.push(row);
  };

  pushSummaryRow("Total Value:", parseFloat(totalValue).toFixed(2));

  if (discountValue) {
    pushSummaryRow("Special Discount:", parseFloat(discountValue).toFixed(2));
    pushSummaryRow(
      "Price After Discount:",
      parseFloat(afterDiscount).toFixed(2)
    );
  }

  if (vatValue > 0) {
    pushSummaryRow(
      `VAT ${first?.dynamicVatFromAPI}%:`,
      parseFloat(vatAmount).toFixed(2)
    );
  }

  pushSummaryRow("Net Value:", parseFloat(netValue).toFixed(2));

  aoa.push([]);

  // ── Terms & Conditions ────────────────────────────────────────────────────
  aoa.push(["Terms & Conditions:"]);
  aoa.push([first?.payment || ""]);
  aoa.push([]);

  // ── Prepared by ───────────────────────────────────────────────────────────
  aoa.push(["Prepared by:"]);
  const preparedByName =
    first?.Coordinator?.trim() &&
    first?.Coordinator?.trim() !== "-" &&
    first?.lead_CoordinatorName
      ? first.lead_CoordinatorName
      : first?.salespersonName || "";
  aoa.push([preparedByName]);

  // ── Build worksheet ───────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths
  const colCount = isCubix ? 7 : 9;
  ws["!cols"] = [
    { wch: 6  }, // S.N
    ...(!isCubix ? [{ wch: 12 }, { wch: 16 }] : []), // Ref/Type, Article No
    { wch: 40 }, // Description
    { wch: 8  }, // Unit
    { wch: 8  }, // Qty
    { wch: 14 }, // Unit Price
    { wch: 14 }, // Value
  ];

  // Style: bold title row (row 1, index 0)
  const titleCell = ws["A1"];
  if (titleCell) {
    titleCell.s = { font: { bold: true, sz: 14 } };
  }

  // Style: bold table header row
  const headerRowNum = tableHeaderRowIndex; // 1-based
  tableHeaderRow.forEach((_, ci) => {
    const cellAddr = XLSX.utils.encode_cell({ r: headerRowNum - 1, c: ci });
    if (!ws[cellAddr]) return;
    ws[cellAddr].s = {
      font: { bold: true },
      fill: { fgColor: { rgb: "CFCFD1" } },
      alignment: { horizontal: "center" },
      border: {
        top:    { style: "thin" },
        bottom: { style: "thin" },
        left:   { style: "thin" },
        right:  { style: "thin" },
      },
    };
  });

  // ── Build workbook & download ─────────────────────────────────────────────
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Quotation");

  const fileName = `${docTitle}_${refNo || "draft"}_${new Date()
    .toISOString()
    .slice(0, 10)}.xlsx`;

  XLSX.writeFile(wb, fileName);
};